<template>
  <div class="flex w-full flex-col text-gray-700">
    <h2 class="mb-4 text-left text-2xl font-bold">Crear Nueva Cotización</h2>
    <form
      class="flex flex-col gap-4"
      @submit="onSubmit"
    >
      <custom-input
        v-model="form.contactName"
        name="name"
        label="Nombre"
        placeholder="Nombre"
        type="text"
        :rules="{ required: true }"
      />
      <custom-input
        v-model="form.contactEmail"
        name="contactEmail"
        label="Email"
        placeholder="Email"
        type="text"
        :rules="{ required: true, email: true }"
      />
      <custom-input
        v-model="form.contactNumber"
        name="contactNumber"
        label="Teléfono"
        placeholder="Teléfono"
        type="text"
      />
      <custom-input
        v-model="form.businessRut"
        name="businessRut"
        label="RUT"
        placeholder="RUT"
        type="text"
        :rules="{ required: true, rut: true }"
      />
      <custom-input
        v-model="form.commercialBusiness"
        name="commercialBusiness"
        label="Giro"
        placeholder="Giro"
        type="text"
        :rules="{ required: true }"
      />
      <custom-input
        v-model="form.socialReason"
        name="socialReason"
        label="Razón Social"
        placeholder="Razón Social"
        type="text"
        :rules="{ required: true }"
      />
      <div class="flex flex-col">
        <lokal-generic-combobox
          v-model="selectedShopKind"
          class="font-light"
          :items="shopKindOptions"
          :get-option-key="(option) => option.id"
          :get-option-label="(option) => option.name"
          :options="shopKindOptions"
          :options-label="`Selecciona un tipo de tienda`"
          :multiple="false"
          :local-search="false"
        />
        <Field
          v-model="form.shopKind"
          name="shopKind"
          as="input"
          type="hidden"
          :rules="{ required: true }"
        />
      </div>
      <div
        v-if="!isRegionsLoading"
        class="flex flex-col"
      >
        <lokal-generic-combobox
          v-model="selectedRegion"
          class="font-light"
          :items="regions"
          :get-option-key="(option: Region) => option.id"
          :get-option-label="(option: Region) => option.name"
          :options="regions"
          :options-label="`Selecciona una región`"
          :multiple="false"
        />
        <Field
          v-model="form.regionId"
          name="regionId"
          as="input"
          type="hidden"
          :rules="{ required: true }"
        />
        <ErrorMessage
          name="regionId"
          class="mt-1 text-xs text-red-500 md:text-sm"
        />
      </div>
      <div
        v-if="!isCommunesLoading"
        class="flex flex-col"
      >
        <lokal-generic-combobox
          v-model="selectedCommune"
          class="font-light"
          :items="communes"
          :get-option-key="(option: Commune) => option.id"
          :get-option-label="(option: Commune) => option.name"
          :options="filteredCommunes"
          :options-label="`Selecciona una comuna`"
          :multiple="false"
        />
        <Field
          v-model="form.communeId"
          name="communeId"
          as="input"
          type="hidden"
          :rules="{ required: true }"
        />
        <ErrorMessage
          name="communeId"
          class="mt-1 text-xs text-red-500 md:text-sm"
        />
      </div>
      <custom-input
        v-model="form.address"
        name="address"
        label="Dirección"
        placeholder="Dirección"
        type="text"
        :rules="{ required: true }"
      />
      <custom-input
        v-model="form.notes"
        name="notes"
        label="Notas"
        placeholder="Notas"
        type="text"
      />
      <custom-input
        v-model="form.expiresAt"
        name="expiresAt"
        label="Fecha de Vencimiento"
        placeholder="Fecha de Vencimiento"
        type="date"
      />
      <div class="flex w-full flex-col items-center">
        <button
          type="submit"
          class="rounded-md border border-lk-green bg-white px-4 py-2 text-sm text-lk-green hover:bg-lk-green hover:text-white md:text-base"
        >
          {{ preOrder?.id ? 'Actualizar Cotización' : 'Crear Cotización' }}
        </button>
        <button
          v-if="preOrder?.id"
          type="button"
          class="mt-1 text-xs hover:underline md:text-sm"
          @click.prevent.stop="emit('move-to:add-products')"
        >
          Continuar a agregar productos
        </button>
      </div>
    </form>
  </div>
</template>
<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useForm, Field, ErrorMessage } from 'vee-validate';
import { useMutation } from 'vue-query';
import preOrdersApi from 'api/pre-orders';
import useMessageStore from 'stores/message-store';
import useRegionsQuery from './queries/regions-query';
import useCommunesQuery from './queries/communes-query';
import LokalGenericCombobox from './shared/lokal-generic-combobox.vue';
import CustomInput from './shared/custom-input.vue';

const props = defineProps<{
  maker: Maker;
  preOrder?: PreOrder;
}>();

interface ShopKindOption {
  id: number;
  name: string;
  type: string;
  description: string;
}

const shopKindOptions = ref([
  {
    id: 4,
    name: 'Tienda física y online',
    type: 'physical_and_online_store',
    description: 'Locación permanente y tienda online',
  },
  { id: 0, name: 'Tienda física', type: 'physical_store', description: 'Locación permanente' },
  { id: 1, name: 'Solo tienda online', type: 'online_store', description: 'Sitio web o redes sociales' },
  { id: 2, name: 'Consumo personal', type: 'personal_consumption', description: 'No permitido' },
  { id: 3, name: 'Otro', type: 'other' },
] as ShopKindOption[]);

const form = ref({
  contactName: props.preOrder?.contactName || '',
  contactEmail: props.preOrder?.contactEmail || '',
  contactNumber: props.preOrder?.contactNumber || '',
  commercialBusiness: props.preOrder?.commercialBusiness || '',
  socialReason: props.preOrder?.socialReason || '',
  businessRut: props.preOrder?.businessRut || '',
  shopKind: props.preOrder?.shopKind || '',
  address: props.preOrder?.address || '',
  communeId: props.preOrder?.communeId || 0,
  regionId: props.preOrder?.regionId || 0,
  notes: props.preOrder?.notes || '',
  expiresAt: props.preOrder?.expiresAt?.split('T')?.[0] || '',
});

const emit = defineEmits<{
  close: [];
  'pre-order:created': [PreOrder];
  'pre-order:updated': [PreOrder];
  'reload:pre-orders': [];
  'move-to:add-products': [];
}>();

const messageStore = useMessageStore();

const selectedRegion = ref({} as Region);
const selectedCommune = ref({} as Commune);
const selectedShopKind = ref({} as ShopKindOption);
const { regions, isLoading: isRegionsLoading } = useRegionsQuery();
const { communes, isLoading: isCommunesLoading } = useCommunesQuery();
const filteredCommunes = computed(() => {
  return communes.value.filter((commune) => commune.regionId === selectedRegion.value.id);
});

watch(selectedRegion, (newRegion) => {
  form.value.regionId = newRegion.id;
});

watch(selectedCommune, (newCommune) => {
  form.value.communeId = newCommune.id;
});

watch(selectedShopKind, (newShopKind) => {
  form.value.shopKind = newShopKind.type;
});

watch(
  regions,
  () => {
    const foundRegion = regions.value?.find((region) => region.id === form.value.regionId);
    if (foundRegion) selectedRegion.value = foundRegion;
  },
  { immediate: true },
);

watch(
  communes,
  () => {
    const foundCommune = communes.value?.find((commune) => commune.id === form.value.communeId);
    if (foundCommune) selectedCommune.value = foundCommune;
  },
  { immediate: true },
);

watch(
  shopKindOptions,
  () => {
    const foundShopKind = shopKindOptions.value.find((shopKind) => shopKind.type === form.value.shopKind);
    if (foundShopKind) selectedShopKind.value = foundShopKind;
  },
  { immediate: true },
);

const createPreOrderMutation = useMutation(
  () => {
    return preOrdersApi.create({ ...form.value, makerId: props.maker.id });
  },
  {
    onSuccess: (data) => {
      const preOrder = data?.data?.preOrder as PreOrder;
      messageStore.showMessage('Cotización creada correctamente', 'success');
      emit('pre-order:created', preOrder);
    },
  },
);

const updatePreOrderMutation = useMutation(
  (preOrderId: number) => {
    return preOrdersApi.update(preOrderId, { ...form.value });
  },
  {
    onSuccess: (data) => {
      const preOrder = data?.data?.preOrder as PreOrder;
      messageStore.showMessage('Cotización actualizada correctamente', 'success');
      emit('pre-order:updated', preOrder);
    },
  },
);

const { handleSubmit } = useForm();

const onSubmit = handleSubmit(() => {
  if (props.preOrder?.id) {
    updatePreOrderMutation.mutate(props.preOrder.id);
  } else {
    createPreOrderMutation.mutate();
  }
});
</script>
